import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, switchMap, tap } from 'rxjs';
import { environment } from '../../../../../environments/environment';

export interface DevToolsStatus {
  available: boolean;
  activeSnapshot: string | null;
  activeEnvironment?: string;
  activeSnapshotDate?: string;
  mongodumpAvailable: boolean;
  mongorestoreAvailable: boolean;
  environments: { name: string; configured: boolean; hasSnapshot: boolean }[];
}

export interface Snapshot {
  id: string;
  type: 'auto' | 'manual';
  environment?: string;
  name: string;
  createdAt: string;
  databases: string[];
}

export interface CurrentOperation {
  type: 'pull' | 'snapshot' | 'switch';
  environment?: string;
  step: string;
  progress: number;
  total: number;
  error?: string;
}

export interface ImpersonationCandidate {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  isPredik: boolean;
}

@Injectable({ providedIn: 'root' })
export class DevToolsService {
  private baseUrl = `${environment.API_URL}/dev-tools`;
  private statusSubject = new BehaviorSubject<DevToolsStatus | null>(null);
  status$ = this.statusSubject.asObservable();

  constructor(private http: HttpClient) {}

  loadStatus(): Observable<DevToolsStatus> {
    return this.http.get<DevToolsStatus>(`${this.baseUrl}/status`).pipe(tap(status => this.statusSubject.next(status)));
  }

  getSnapshots(): Observable<Snapshot[]> {
    return this.http.get<Snapshot[]>(`${this.baseUrl}/snapshots`);
  }

  pull(env: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/pull/${env}`, {});
  }

  createSnapshot(name: string): Observable<Snapshot> {
    return this.http.post<Snapshot>(`${this.baseUrl}/snapshot`, { name });
  }

  switchSnapshot(snapshotId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/switch/${snapshotId}`, {});
  }

  deleteSnapshot(snapshotId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/snapshot/${snapshotId}`);
  }

  getCurrentOperation(): Observable<CurrentOperation | null> {
    return this.http.get<CurrentOperation | null>(`${this.baseUrl}/operations/current`);
  }

  clearOperation(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/operations/clear`, {});
  }

  pollOperation(): Observable<CurrentOperation | null> {
    return interval(2000).pipe(switchMap(() => this.getCurrentOperation()));
  }

  // ─── Substitution d'identité ───

  getImpersonationCandidates(search: string): Observable<ImpersonationCandidate[]> {
    return this.http.get<ImpersonationCandidate[]>(`${this.baseUrl}/impersonation/candidates`, { params: { search } });
  }

  startImpersonation(userProfileId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/impersonation/${userProfileId}`, {});
  }

  stopImpersonation(): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/impersonation`);
  }
}
